import {
  createContext,
  type ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  getInitialTheme,
  THEME_STORAGE_KEY,
  type Theme,
} from '@/constants/theme';
import { useBoundStore } from '@/stores/store';

type ThemeContextValue = {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;
};

const ThemeContext = createContext<ThemeContextValue | null>(null);

type ThemeProviderProps = {
  children: ReactNode;
};

const applyThemeClass = (theme: Theme) => {
  if (typeof document === 'undefined') {
    return;
  }

  const root = document.documentElement;
  root.classList.remove('dark');

  if (theme === 'dark') {
    root.classList.add('dark');
  }
};

export function ThemeProvider({ children }: ThemeProviderProps) {
  const [theme, setThemeState] = useState<Theme>(getInitialTheme);
  const setUserTheme = useBoundStore((state) => state.setUserTheme);

  useEffect(() => {
    applyThemeClass(theme);
    window.localStorage.setItem(THEME_STORAGE_KEY, theme);
    setUserTheme(theme);
  }, [setUserTheme, theme]);

  const value = useMemo<ThemeContextValue>(
    () => ({
      theme,
      setTheme: (nextTheme) => {
        const changeTheme = () => {
          setThemeState(nextTheme);
        };

        if (
          typeof document !== 'undefined' &&
          'startViewTransition' in document
        ) {
          (
            document as Document & {
              startViewTransition: (callback: () => void) => void;
            }
          ).startViewTransition(changeTheme);
        } else {
          changeTheme();
        }
      },
      toggleTheme: () => {
        const changeTheme = () => {
          setThemeState((current) => (current === 'dark' ? 'light' : 'dark'));
        };

        if (
          typeof document !== 'undefined' &&
          'startViewTransition' in document
        ) {
          (
            document as Document & {
              startViewTransition: (callback: () => void) => void;
            }
          ).startViewTransition(changeTheme);
        } else {
          changeTheme();
        }
      },
    }),
    [theme],
  );

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);

  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }

  return context;
}
